/**
 * Vault → Receive: pick the network → the vault's own address for it (QR, copy, clear network
 * label). Addresses come from the unlocked vault on this device, not from the server.
 */
import { Card, CardTitle, CopyButton, Notice, QrCode, cn } from "@custos/ui";
import { Check } from "lucide-react";
import { Navigate, useSearchParams } from "react-router";
import { PageHeader } from "../components/AppShell";
import { AssetIcon } from "../components/bits";
import { FAMILY_KEY, findChain, networkLabel } from "../lib/chains";
import { useConfig } from "../lib/queries";
import { useVaultSession } from "../lib/vault-ui";

export function VaultReceivePage() {
  const config = useConfig();
  const vault = useVaultSession();
  const [params, setParams] = useSearchParams();
  if (vault.status !== "unlocked" && vault.status !== "loading") return <Navigate to="/vault" replace />;

  const assets = config.data?.assets ?? [];
  const chains = (config.data?.chains ?? []).filter((c) =>
    vault.accounts.some((a) => a.family === FAMILY_KEY[c.family]),
  );
  const selected = findChain(chains, params.get("chain") ?? "");
  const account = selected ? vault.accounts.find((a) => a.family === FAMILY_KEY[selected.family]) : undefined;
  const chainAssets = assets.filter((a) => a.chain === selected?.key);

  return (
    <>
      <PageHeader title="Receive to vault" subtitle="Choose the network, then send to this address." />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <Card className="p-2 sm:p-2">
          <p
            className="px-3 pt-2 pb-1 text-xs font-medium tracking-wide text-muted uppercase"
            id="vault-network"
          >
            Network
          </p>
          <div role="radiogroup" aria-labelledby="vault-network" className="flex flex-col gap-1">
            {chains.map((c) => {
              const active = c.key === selected?.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setParams({ chain: c.key }, { replace: true })}
                  className={cn(
                    "flex min-h-14 items-center gap-3 rounded-field px-3 text-left transition-colors duration-150",
                    active ? "bg-primary-soft ring-2 ring-primary" : "hover:bg-hover",
                  )}
                >
                  <AssetIcon symbol={c.nativeSymbol} className="size-10 text-base" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-fg">{c.displayName}</span>
                    <span className="block truncate text-xs text-muted">
                      {c.network} ·{" "}
                      {assets
                        .filter((a) => a.chain === c.key)
                        .map((a) => a.symbol)
                        .join(", ")}
                    </span>
                  </span>
                  {active && <Check className="size-4 text-primary-strong" aria-hidden />}
                </button>
              );
            })}
          </div>
        </Card>

        <Card>
          {!selected || !account ? (
            <div className="flex min-h-48 items-center justify-center text-center text-sm text-muted">
              Pick a network to see your vault address.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <CardTitle>Your vault {selected.displayName} address</CardTitle>
                <p className="mt-1 text-sm text-muted">
                  Network:{" "}
                  <span className="font-medium text-fg" data-testid="vault-receive-network">
                    {networkLabel(selected)}
                  </span>
                </p>
              </div>
              <Notice
                tone="warning"
                title={`Only send ${chainAssets.map((a) => a.symbol).join(" or ")} on ${networkLabel(selected)}`}
              >
                This is your <b>self-custody vault</b> address, not your Account deposit address. Coins sent
                on another network may be lost.
              </Notice>
              <div className="flex flex-col items-center gap-4">
                <QrCode
                  value={account.address}
                  label={`QR code of your vault ${selected.displayName} address`}
                />
                <div className="w-full rounded-field border border-line bg-soft p-3">
                  <p className="mb-1 text-xs text-muted">Address</p>
                  <div className="flex items-start gap-2">
                    <code
                      className="min-w-0 flex-1 font-mono text-sm break-all text-fg"
                      data-testid="vault-receive-address"
                    >
                      {account.address}
                    </code>
                    <CopyButton value={account.address} label="Copy address" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
